class HeaderPostcode extends HTMLElement {
    static postcodeIndexPromise = null;
    static postcodeLocationList = null;
    static ipLocationPromise = null;

    constructor() {
        super();
        this.cookieKey = 'postcode_search';
        this.locationCookieKey = 'postcode_location';
        this.ipLocationAttemptKey = 'postcode_ip_location_attempted_at_v4';
        this.ipLocationAttemptTtl = 24 * 3600 * 1000;
        this.details = this.querySelector('details');
        this.summary = this.querySelector('summary');
        this.form = this.querySelector('[data-postcode-form]');
        this.input = this.querySelector('[data-postcode-input]');
        this.label = this.querySelector('[data-postcode-label]');
        this.labelPrefix = this.querySelector('[data-postcode-label-prefix]');
        this.labelValue = this.querySelector('[data-postcode-label-value]');
        this.error = this.querySelector('[data-postcode-error]');
        this.suggestions = this.querySelector('[data-postcode-suggestions]');
        this.postcodesUrl = this.dataset.postcodesUrl || '';
        this.countryCode = (this.dataset.countryCode || '').toUpperCase();
        this.isAustralia = this.countryCode === 'AU';
        this.postcodeLength = parseInt(this.dataset.postcodeLength, 10) || (this.countryCode === 'US' ? 5 : 4);
        this.defaultErrorMessage = this.error?.textContent?.trim() || 'Something went wrong. Please check shipping rates in cart or at checkout. If it is still not working, please contact us.';
        this.defaultLocation = this.getDefaultLocation();
        this.selectedLocation = null;
        this.lookupRequestId = 0;

        this.handleSubmit = this.onSubmit.bind(this);
        this.handleWindowUpdate = this.onWindowUpdate.bind(this);
        this.handleDocumentClick = this.onDocumentClick.bind(this);
        this.handleInput = this.onInput.bind(this);
        this.handleSuggestionClick = this.onSuggestionClick.bind(this);
        this.handleSummaryClick = this.onSummaryClick.bind(this);
        this.handleToggle = this.onToggle.bind(this);
        this.skipNextOpenToggle = false;
    }

    connectedCallback() {
        this.hydrateFromDefaultLocation();
        this.syncFromCookie();
        this.scheduleIpLocationLookup();
        this.form?.addEventListener('submit', this.handleSubmit);
        this.summary?.addEventListener('click', this.handleSummaryClick);
        window.addEventListener('postcode:updated', this.handleWindowUpdate);
        document.addEventListener('click', this.handleDocumentClick);

        if (this.isAustralia) {
            this.input?.addEventListener('input', this.handleInput);
            this.input?.addEventListener('focus', this.handleInput);
            this.suggestions?.addEventListener('click', this.handleSuggestionClick);
            this.details?.addEventListener('toggle', this.handleToggle);
        }
    }

    disconnectedCallback() {
        this.form?.removeEventListener('submit', this.handleSubmit);
        this.summary?.removeEventListener('click', this.handleSummaryClick);
        window.removeEventListener('postcode:updated', this.handleWindowUpdate);
        document.removeEventListener('click', this.handleDocumentClick);

        if (this.isAustralia) {
            this.input?.removeEventListener('input', this.handleInput);
            this.input?.removeEventListener('focus', this.handleInput);
            this.suggestions?.removeEventListener('click', this.handleSuggestionClick);
            this.details?.removeEventListener('toggle', this.handleToggle);
        }
    }

    hydrateFromDefaultLocation() {
        if (!this.isAustralia || !this.defaultLocation) return;
        if (this.getCookieValue(this.cookieKey) || this.getStoredLocation()) return;

        this.persistLocation(this.defaultLocation, { close: false });
    }

    scheduleIpLocationLookup() {
        if (!this.shouldUseIpLocationLookup()) return;

        const runLookup = () => {
            this.hydrateFromIpLocation().catch((error) => {
                console.warn('IP postcode lookup failed', error);
            });
        };

        if ('requestIdleCallback' in window) {
            window.requestIdleCallback(runLookup, { timeout: 3000 });
            return;
        }

        window.setTimeout(runLookup, 700);
    }

    shouldUseIpLocationLookup() {
        if (!this.input || !this.isAustralia) return false;
        if (this.getActivePostcode()) return false;
        if (!HeaderPostcode.ipLocationPromise && this.hasRecentIpLocationAttempt()) return false;

        return true;
    }

    getActivePostcode() {
        return this.normalizeLookupPostcode(
            this.selectedLocation?.postcode || this.getCookieValue(this.cookieKey) || this.input?.value || ''
        );
    }

    async hydrateFromIpLocation() {
        if (!this.shouldUseIpLocationLookup()) return;

        const location = await this.resolveIpLocation();
        if (!location || this.getActivePostcode()) return;

        this.persistLocation(location);
    }

    async resolveIpLocation() {
        if (!HeaderPostcode.ipLocationPromise) {
            if (this.hasRecentIpLocationAttempt()) return null;
            this.markIpLocationAttempt();
        }

        const ipLocation = await HeaderPostcode.fetchIpApiLocation();
        return this.normalizeIpLocation(ipLocation);
    }

    async normalizeIpLocation(ipLocation) {
        if (!ipLocation || ipLocation.error) return null;

        const locationData = Array.isArray(ipLocation) ? ipLocation[0] : ipLocation;
        if (!locationData) return null;

        const ipifyLocation = locationData.location || {};
        const countryCode = (ipifyLocation.country || locationData.countryCode || locationData.country_code || locationData.country || '').toUpperCase();
        if (countryCode && countryCode !== 'AU') return null;

        const postcode = this.normalizeLookupPostcode(locationData.ipapiPostal || ipifyLocation.postalCode || locationData.zipCode || locationData.postal_code || locationData.postal || locationData.postcode || locationData.zip || '');
        if (!this.isValidRawPostcode(postcode)) return null;

        const locations = await this.findLocationsByPostcode(postcode);

        if (Array.isArray(locations) && locations.length) {
            return locations[0];
        }

        return null;
    }

    static async fetchIpApiLocation() {
        if (!HeaderPostcode.ipLocationPromise) {
            HeaderPostcode.ipLocationPromise = fetch('https://ipapi.co/postal/', {
                credentials: 'omit',
                headers: {
                    Accept: 'text/plain'
                }
            })
                .then((response) => {
                    if (!response.ok) {
                        throw new Error(`ipapi postal lookup failed: ${response.status}`);
                    }

                    return response.text();
                })
                .then((postal) => {
                    return {
                        ipapiPostal: postal
                    };
                })
                .catch((error) => {
                    console.warn('ipapi postal lookup failed', error);
                    return null;
                });
        }

        return HeaderPostcode.ipLocationPromise;
    }

    async onSubmit(event) {
        event.preventDefault();
        const searchValue = this.normalizeSearchValue(this.input?.value || '').trim();

        if (!searchValue) {
            this.showError(this.defaultErrorMessage);
            return;
        }

        if (!this.isAustralia) {
            const postcode = this.normalizePostcode(searchValue);
            if (!this.isValidRawPostcode(postcode)) {
                this.showError(this.defaultErrorMessage);
                return;
            }

            this.persistRawPostcode(postcode);
            return;
        }

        const locations = await this.findLocationsBySearch(searchValue);

        if (locations === null) {
            const postcode = this.normalizePostcode(searchValue);
            const province = this.getProvinceCode(postcode);
            if (this.isValidRawPostcode(postcode) && province) {
                this.persistRawPostcode(postcode, province);
                return;
            }

            this.showError(this.defaultErrorMessage);
            return;
        }

        if (locations.length === 1) {
            this.persistLocation(locations[0]);
            return;
        }

        if (locations.length > 1) {
            this.renderSuggestions(locations);
            this.showError('Please select your suburb from the list.');
            return;
        }

        this.showError(this.defaultErrorMessage);
    }

    onWindowUpdate(event) {
        const detail = event?.detail || {};
        const location = detail.suburb && detail.province
            ? {
                suburb: detail.suburb,
                state: detail.province,
                postcode: detail.postcode
            }
            : this.getStoredLocation();
        const postcode = detail.postcode || location?.postcode || this.getCookieValue(this.cookieKey) || '';

        this.selectedLocation = location;
        this.updateUi(location || postcode);
    }

    onDocumentClick(event) {
        if (this.details?.hasAttribute('open') && !this.contains(event.target)) {
            this.details.removeAttribute('open');
        }
    }

    onSummaryClick(event) {
        if (!this.shouldUseImmediateFocus() || !this.details || this.details.open) return;

        event.preventDefault();
        this.skipNextOpenToggle = true;
        this.details.open = true;
        this.prepareOpenState({ immediateFocus: true });
    }

    onToggle() {
        if (!this.details?.open) {
            this.hideSuggestions();
            return;
        }

        if (this.skipNextOpenToggle) {
            this.skipNextOpenToggle = false;
            return;
        }

        this.prepareOpenState({ immediateFocus: false });
    }

    prepareOpenState({ immediateFocus = false } = {}) {
        if (this.input) {
            this.input.value = '';

            const focusInput = () => {
                try {
                    this.input.focus({ preventScroll: true });
                } catch (error) {
                    this.input.focus();
                }
            };

            if (immediateFocus) {
                focusInput();
            } else {
                requestAnimationFrame(focusInput);
            }
        }

        this.hideError();
        this.hideSuggestions();
    }

    shouldUseImmediateFocus() {
        const platform = navigator.platform || '';
        const userAgent = navigator.userAgent || '';

        return /iPad|iPhone|iPod/.test(userAgent) || (platform === 'MacIntel' && navigator.maxTouchPoints > 1);
    }

    async onInput() {
        if (!this.input) return;

        const searchValue = this.normalizeSearchValue(this.input.value);
        if (searchValue !== this.input.value) {
            this.input.value = searchValue;
        }

        if (!this.isAustralia) return;

        if (!this.selectedLocation || !this.isLocationMatchForSearch(this.selectedLocation, searchValue)) {
            this.selectedLocation = null;
        }

        this.hideError();

        if (!this.shouldSearchLocations(searchValue)) {
            this.hideSuggestions();
            return;
        }

        const requestId = this.lookupRequestId + 1;
        this.lookupRequestId = requestId;

        const locations = await this.findLocationsBySearch(searchValue);
        if (requestId !== this.lookupRequestId) return;

        if (locations) {
            this.renderSuggestions(locations);
        } else {
            this.hideSuggestions();
        }
    }

    onSuggestionClick(event) {
        const trigger = event.target.closest('[data-postcode-suggestion]');
        if (!trigger) return;

        event.preventDefault();

        const location = {
            suburb: trigger.dataset.suburb || '',
            state: trigger.dataset.state || '',
            postcode: trigger.dataset.postcode || ''
        };

        if (!location.suburb || !location.state || !location.postcode) return;
        this.persistLocation(location);
    }

    syncFromCookie() {
        const storedLocation = this.getStoredLocation();
        const storedPostcode = this.getCookieValue(this.cookieKey) || '';

        this.selectedLocation = storedLocation;
        this.updateUi(storedLocation || storedPostcode);
    }

    updateUi(locationOrPostcode) {
        const location = typeof locationOrPostcode === 'object' && locationOrPostcode !== null
            ? locationOrPostcode
            : null;
        const postcode = location?.postcode || locationOrPostcode || '';

        if (this.input && document.activeElement !== this.input) {
            this.input.value = postcode;
        }

        this.updateLabel(location, postcode);

        this.hideError();
    }

    updateLabel(location, postcode) {
        if (!this.label) return;

        if (!this.labelPrefix || !this.labelValue) {
            this.label.textContent = this.getDisplayLabel(location, postcode);
            return;
        }

        if (!postcode) {
            this.label.classList.remove('is-split');
            this.labelPrefix.textContent = 'Enter postcode or suburb';
            this.labelValue.textContent = '';
            this.labelValue.hidden = true;
            return;
        }

        this.label.classList.add('is-split');
        this.labelPrefix.textContent = 'Deliver to ';
        this.labelValue.textContent = location?.suburb && location?.state && location?.postcode
            ? `${location.suburb}, ${location.state} ${location.postcode}`
            : postcode;
        this.labelValue.hidden = false;
    }

    getDisplayLabel(location, postcode) {
        if (location?.suburb && location?.state && location?.postcode) {
            return `Deliver to ${location.suburb}, ${location.state} ${location.postcode}`;
        }

        return postcode ? `Deliver to ${postcode}` : 'Enter postcode or suburb';
    }

    persistRawPostcode(postcode, province = null) {
        this.selectedLocation = null;
        this.clearStoredLocation();
        this.setCookieValue(this.cookieKey, postcode, 31 * 24 * 3600 * 1000);
        this.updateUi(postcode);
        this.hideSuggestions();
        this.details?.removeAttribute('open');
        const detail = {
            postcode,
            countryCode: this.countryCode || 'AU'
        };

        if (province) {
            detail.province = province;
        }

        this.dispatchUpdate(detail);
    }

    persistLocation(location, options = {}) {
        const normalizedLocation = {
            suburb: location.suburb || '',
            state: (location.state || '').toUpperCase(),
            postcode: this.normalizeLookupPostcode(location.postcode)
        };

        this.selectedLocation = normalizedLocation;
        this.setCookieValue(this.cookieKey, normalizedLocation.postcode, 31 * 24 * 3600 * 1000);
        this.setStoredLocation(normalizedLocation);
        this.updateUi(normalizedLocation);
        this.hideSuggestions();

        if (options.close !== false) {
            this.details?.removeAttribute('open');
        }

        this.dispatchUpdate({
            postcode: normalizedLocation.postcode,
            province: normalizedLocation.state,
            suburb: normalizedLocation.suburb,
            label: this.getDisplayLabel(normalizedLocation),
            countryCode: 'AU'
        });
    }

    dispatchUpdate(detail) {
        window.dispatchEvent(
            new CustomEvent('postcode:updated', {
                detail
            })
        );
    }

    getDefaultLocation() {
        const suburb = (this.dataset.defaultSuburb || '').trim();
        const state = (this.dataset.defaultState || '').trim().toUpperCase();
        const postcode = this.normalizeLookupPostcode(this.dataset.defaultPostcode || '');

        if (!suburb || !state || !postcode) return null;

        return {
            suburb,
            state,
            postcode
        };
    }

    getStoredLocation() {
        const cookieValue = this.getCookieValue(this.locationCookieKey);
        if (!cookieValue) return null;

        try {
            const parsedValue = JSON.parse(decodeURIComponent(cookieValue));
            if (!parsedValue?.postcode || !parsedValue?.state) return null;

            return {
                suburb: parsedValue.suburb || '',
                state: parsedValue.state,
                postcode: this.normalizeLookupPostcode(parsedValue.postcode)
            };
        } catch (error) {
            return null;
        }
    }

    setStoredLocation(location) {
        this.setCookieValue(
            this.locationCookieKey,
            encodeURIComponent(JSON.stringify(location)),
            31 * 24 * 3600 * 1000
        );
    }

    clearStoredLocation() {
        this.deleteCookie(this.locationCookieKey);
    }

    isValidRawPostcode(postcode) {
        return Boolean(postcode) && postcode.length === this.postcodeLength;
    }

    normalizePostcode(value) {
        return (value || '').replace(/\D/g, '').slice(0, this.postcodeLength);
    }

    normalizeSearchValue(value) {
        const normalizedValue = (value || '')
            .replace(/[^0-9A-Za-z\s'.-]/g, '')
            .replace(/\s{2,}/g, ' ')
            .trimStart()
            .slice(0, 50);

        return this.isNumericSearchValue(normalizedValue)
            ? normalizedValue.slice(0, this.postcodeLength)
            : normalizedValue;
    }

    normalizeLookupPostcode(value) {
        const normalizedValue = this.normalizePostcode(value);
        if (!normalizedValue) return '';

        if (!this.isAustralia || normalizedValue.length >= this.postcodeLength) return normalizedValue;

        const parsedValue = parseInt(normalizedValue, 10);
        return !Number.isNaN(parsedValue) && parsedValue >= 200
            ? normalizedValue.padStart(this.postcodeLength, '0')
            : normalizedValue;
    }

    getLocationKey(location) {
        return [location.suburb, location.state, location.postcode].join('|');
    }

    isNumericSearchValue(value) {
        return /^\d+$/.test((value || '').trim());
    }

    shouldSearchLocations(value) {
        const searchValue = (value || '').trim();
        if (!searchValue) return false;

        return this.isNumericSearchValue(searchValue)
            ? searchValue.length >= this.postcodeLength
            : HeaderPostcode.normalizeSearchText(searchValue).length >= 2;
    }

    isLocationMatchForSearch(location, value) {
        if (!location || !value) return false;

        const searchValue = value.trim();
        if (this.isNumericSearchValue(searchValue)) {
            return location.postcode === this.normalizeLookupPostcode(searchValue);
        }

        return HeaderPostcode.normalizeSearchText(location.suburb).includes(HeaderPostcode.normalizeSearchText(searchValue));
    }

    renderSuggestions(locations) {
        if (!this.suggestions) return;

        if (!locations?.length) {
            this.hideSuggestions();
            return;
        }

        const activeKey = this.selectedLocation ? this.getLocationKey(this.selectedLocation) : '';

        this.suggestions.innerHTML = locations.map((location) => {
            const locationKey = this.getLocationKey(location);
            const activeClass = locationKey === activeKey ? ' is-active' : '';
            const locationLabel = this.escapeAttribute(`${location.postcode} ${location.suburb}, ${location.state}`);

            return `
                <button
                    class="header-v2-postcode-suggestion${activeClass}"
                    type="button"
                    data-postcode-suggestion
                    data-suburb="${this.escapeAttribute(location.suburb)}"
                    data-state="${this.escapeAttribute(location.state)}"
                    data-postcode="${this.escapeAttribute(location.postcode)}"
                >
                    ${locationLabel}
                </button>
            `;
        }).join('');

        this.classList.add('has-postcode-suggestions');
        this.suggestions.hidden = false;
    }

    hideSuggestions() {
        if (!this.suggestions) return;
        this.classList.remove('has-postcode-suggestions');
        this.suggestions.hidden = true;
        this.suggestions.innerHTML = '';
    }

    showError(message) {
        if (!this.error) return;
        this.error.textContent = message;
        this.error.hidden = false;
    }

    hideError() {
        if (!this.error) return;
        this.error.textContent = this.defaultErrorMessage;
        this.error.hidden = true;
    }

    escapeAttribute(value) {
        return String(value)
            .replace(/&/g, '&amp;')
            .replace(/"/g, '&quot;')
            .replace(/</g, '&lt;')
            .replace(/>/g, '&gt;');
    }

    async findLocationsByPostcode(postcode) {
        if (!this.isAustralia || !this.postcodesUrl) return [];

        try {
            const postcodeIndex = await HeaderPostcode.loadPostcodeIndex(this.postcodesUrl);
            return postcodeIndex.get(this.normalizeLookupPostcode(postcode)) || [];
        } catch (error) {
            return null;
        }
    }

    async findLocationsBySearch(value) {
        const searchValue = this.normalizeSearchValue(value).trim();
        if (!searchValue) return [];

        if (this.isNumericSearchValue(searchValue)) {
            return this.findLocationsByPostcode(searchValue);
        }

        return this.findLocationsBySuburb(searchValue);
    }

    async findLocationsBySuburb(value) {
        if (!this.isAustralia || !this.postcodesUrl) return [];

        try {
            const postcodeIndex = await HeaderPostcode.loadPostcodeIndex(this.postcodesUrl);
            const allLocations = HeaderPostcode.getLocationsFromPostcodeIndex(postcodeIndex);
            const searchValue = HeaderPostcode.normalizeSearchText(value);
            const startsWithMatches = [];
            const containsMatches = [];

            allLocations.forEach((location) => {
                const suburb = HeaderPostcode.normalizeSearchText(location.suburb);
                if (suburb.startsWith(searchValue)) {
                    startsWithMatches.push(location);
                    return;
                }

                if (suburb.includes(searchValue)) {
                    containsMatches.push(location);
                }
            });

            return startsWithMatches.concat(containsMatches).slice(0, 12);
        } catch (error) {
            return null;
        }
    }

    static async loadPostcodeIndex(url) {
        if (!HeaderPostcode.postcodeIndexPromise) {
            HeaderPostcode.postcodeIndexPromise = fetch(url)
                .then((response) => {
                    if (!response.ok) {
                        throw new Error(`Failed to load postcode lookup data: ${response.status}`);
                    }

                    return response.text();
                })
                .then((csvText) => HeaderPostcode.parsePostcodeCsv(csvText));
        }

        return HeaderPostcode.postcodeIndexPromise;
    }

    static parsePostcodeCsv(csvText) {
        const postcodeIndex = new Map();
        const seenLocations = new Set();
        const rows = csvText.split(/\r?\n/).slice(1);

        rows.forEach((row) => {
            if (!row) return;

            const [rawSuburb, rawState, rawPostcode] = row.split(',');
            if (!rawSuburb || !rawState || !rawPostcode) return;

            const postcode = HeaderPostcode.normalizeCsvPostcode(rawPostcode);
            if (!postcode) return;

            const location = {
                suburb: HeaderPostcode.formatSuburb(rawSuburb),
                state: rawState.trim().toUpperCase(),
                postcode
            };
            const locationKey = `${location.suburb}|${location.state}|${location.postcode}`;

            if (seenLocations.has(locationKey)) return;
            seenLocations.add(locationKey);

            if (!postcodeIndex.has(postcode)) {
                postcodeIndex.set(postcode, []);
            }

            postcodeIndex.get(postcode).push(location);
        });

        postcodeIndex.forEach((locations) => {
            locations.sort((locationA, locationB) => locationA.suburb.localeCompare(locationB.suburb));
        });

        return postcodeIndex;
    }

    static getLocationsFromPostcodeIndex(postcodeIndex) {
        if (HeaderPostcode.postcodeLocationList) {
            return HeaderPostcode.postcodeLocationList;
        }

        const locations = [];

        postcodeIndex.forEach((postcodeLocations) => {
            locations.push(...postcodeLocations);
        });

        HeaderPostcode.postcodeLocationList = locations.sort((locationA, locationB) => {
            return locationA.suburb.localeCompare(locationB.suburb) || locationA.postcode.localeCompare(locationB.postcode);
        });

        return HeaderPostcode.postcodeLocationList;
    }

    static normalizeCsvPostcode(value) {
        const postcode = (value || '').trim().replace(/\D/g, '');
        if (!postcode) return '';

        return postcode.length < 4 ? postcode.padStart(4, '0') : postcode.slice(0, 4);
    }

    static formatSuburb(value) {
        return (value || '')
            .trim()
            .toLowerCase()
            .replace(/(^|[\s\-'])([a-z])/g, (match, prefix, character) => `${prefix}${character.toUpperCase()}`);
    }

    static normalizeSearchText(value) {
        return (value || '')
            .toLowerCase()
            .normalize('NFD')
            .replace(/[\u0300-\u036f]/g, '')
            .replace(/[^a-z0-9]+/g, ' ')
            .trim();
    }

    setCookieValue(name, value, miliseconds = 1) {
        const date = new Date();
        date.setTime(date.getTime() + miliseconds);
        const expires = `expires=${date.toUTCString()}`;
        document.cookie = `${name}=${value};${expires};path=/`;
    }

    deleteCookie(name) {
        document.cookie = `${name}=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=/`;
    }

    getCookieValue(name) {
        const cookieArr = document.cookie.split(';');
        for (let i = 0; i < cookieArr.length; i += 1) {
            const cookie = cookieArr[i].trim();
            if (cookie.indexOf(`${name}=`) === 0) {
                return cookie.substring((`${name}=`).length, cookie.length);
            }
        }
        return null;
    }

    getLocalStorageValue(name) {
        try {
            return window.localStorage.getItem(name);
        } catch (error) {
            return null;
        }
    }

    setLocalStorageValue(name, value) {
        try {
            window.localStorage.setItem(name, value);
        } catch (error) {}
    }

    getProvinceCode(zipcode) {
        const parsedZipcode = parseInt(zipcode, 10);

        if ((parsedZipcode >= 1000 && parsedZipcode <= 1999) || (parsedZipcode >= 2000 && parsedZipcode <= 2599) || (parsedZipcode >= 2619 && parsedZipcode <= 2899) || (parsedZipcode >= 2921 && parsedZipcode <= 2999)) {
            return 'NSW';
        }
        if ((parsedZipcode >= 200 && parsedZipcode <= 299) || (parsedZipcode >= 2600 && parsedZipcode <= 2618) || (parsedZipcode >= 2900 && parsedZipcode <= 2920)) {
            return 'ACT';
        }
        if ((parsedZipcode >= 3000 && parsedZipcode <= 3999) || (parsedZipcode >= 8000 && parsedZipcode <= 8999)) {
            return 'VIC';
        }
        if ((parsedZipcode >= 4000 && parsedZipcode <= 4999) || (parsedZipcode >= 9000 && parsedZipcode <= 9999)) {
            return 'QLD';
        }
        if ((parsedZipcode >= 5000 && parsedZipcode <= 5799) || (parsedZipcode >= 5800 && parsedZipcode <= 5999)) {
            return 'SA';
        }
        if ((parsedZipcode >= 6000 && parsedZipcode <= 6797) || (parsedZipcode >= 6800 && parsedZipcode <= 6999)) {
            return 'WA';
        }
        if ((parsedZipcode >= 7000 && parsedZipcode <= 7799) || (parsedZipcode >= 7800 && parsedZipcode <= 7999)) {
            return 'TAS';
        }
        if ((parsedZipcode >= 800 && parsedZipcode <= 899) || (parsedZipcode >= 900 && parsedZipcode <= 999)) {
            return 'NT';
        }

        return null;
    }

    normalizeStateCode(value) {
        const normalizedValue = (value || '').trim().toUpperCase();
        const compactValue = normalizedValue.replace(/[^A-Z]/g, '');
        const stateMap = {
            ACT: 'ACT',
            AUSTRALIANCAPITALTERRITORY: 'ACT',
            NSW: 'NSW',
            NEWSOUTHWALES: 'NSW',
            NT: 'NT',
            NORTHERNTERRITORY: 'NT',
            QLD: 'QLD',
            QUEENSLAND: 'QLD',
            SA: 'SA',
            SOUTHAUSTRALIA: 'SA',
            TAS: 'TAS',
            TASMANIA: 'TAS',
            VIC: 'VIC',
            VICTORIA: 'VIC',
            WA: 'WA',
            WESTERNAUSTRALIA: 'WA'
        };

        return stateMap[compactValue] || '';
    }

    hasRecentIpLocationAttempt() {
        const attemptedAt = parseInt(this.getLocalStorageValue(this.ipLocationAttemptKey) || '0', 10);
        if (!attemptedAt) return false;

        return Date.now() - attemptedAt < this.ipLocationAttemptTtl;
    }

    markIpLocationAttempt() {
        this.setLocalStorageValue(this.ipLocationAttemptKey, Date.now().toString());
    }
}

if (!customElements.get('header-postcode')) {
    customElements.define('header-postcode', HeaderPostcode);
}

function syncHeaderV2SearchState() {
    document.body.classList.toggle(
        'header-v2-search-open',
        Boolean(document.querySelector('.section-header-v2 .header-v2-search details[open]'))
    );
}

function bindHeaderV2SearchState(root = document) {
    root.querySelectorAll('.section-header-v2 .header-v2-search details').forEach((details) => {
        if (details.dataset.headerV2SearchBound === 'true') return;

        details.dataset.headerV2SearchBound = 'true';
        details.addEventListener('toggle', syncHeaderV2SearchState);
    });

    syncHeaderV2SearchState();
}

function bindHeaderV2MobileNavDrag(root = document) {
    root.querySelectorAll('.section-header-v2 .header-v2-mobile-nav').forEach((nav) => {
        if (nav.dataset.headerV2NavDragBound === 'true') return;

        nav.dataset.headerV2NavDragBound = 'true';

        let activePointerId = null;
        let startX = 0;
        let startScrollLeft = 0;
        let hasDragged = false;
        let suppressClickTimeout = null;

        const clearSuppressClick = () => {
            if (suppressClickTimeout) {
                window.clearTimeout(suppressClickTimeout);
            }

            suppressClickTimeout = window.setTimeout(() => {
                delete nav.dataset.headerV2SuppressClick;
                suppressClickTimeout = null;
            }, 0);
        };

        const endDrag = () => {
            if (activePointerId !== null) {
                try {
                    nav.releasePointerCapture(activePointerId);
                } catch (error) {
                    // Ignore release errors when the pointer capture is already gone.
                }
            }

            activePointerId = null;
            nav.classList.remove('is-dragging');

            if (hasDragged) {
                nav.dataset.headerV2SuppressClick = 'true';
                clearSuppressClick();
            }
        };

        nav.addEventListener('pointerdown', (event) => {
            if (event.pointerType === 'touch' || event.button !== 0) return;

            activePointerId = event.pointerId;
            startX = event.clientX;
            startScrollLeft = nav.scrollLeft;
            hasDragged = false;
        });

        nav.addEventListener('pointermove', (event) => {
            if (activePointerId !== event.pointerId) return;

            const deltaX = event.clientX - startX;

            if (!hasDragged && Math.abs(deltaX) > 6) {
                hasDragged = true;
                nav.classList.add('is-dragging');
                try {
                    nav.setPointerCapture(activePointerId);
                } catch (error) {
                    // Ignore capture errors when the browser does not allow it.
                }
            }

            if (!hasDragged) return;

            nav.scrollLeft = startScrollLeft - deltaX;
            event.preventDefault();
        });

        nav.addEventListener('pointerup', (event) => {
            if (activePointerId !== event.pointerId) return;
            endDrag();
        });

        nav.addEventListener('pointercancel', (event) => {
            if (activePointerId !== event.pointerId) return;
            endDrag();
        });

        nav.addEventListener('dragstart', (event) => {
            if (activePointerId === null) return;
            event.preventDefault();
        });

        nav.addEventListener('selectstart', (event) => {
            if (activePointerId === null) return;
            event.preventDefault();
        });

        nav.addEventListener('click', (event) => {
            if (nav.dataset.headerV2SuppressClick !== 'true') return;

            event.preventDefault();
            event.stopPropagation();
            delete nav.dataset.headerV2SuppressClick;
        }, true);
    });
}

function bindHeaderV2DesktopDropdowns(root = document) {
    root.querySelectorAll('.section-header-v2 .header-v2-desktop-bottom .header__inline-menu .menu-level-1').forEach((menu) => {
        if (menu.dataset.headerV2DropdownBound === 'true') return;

        menu.dataset.headerV2DropdownBound = 'true';
        const submenuItems = menu.querySelectorAll('.menu-level-2 > li');
        const scrollableMenus = menu.querySelectorAll('.menu-level-2, .menu-level-3');

        const updateDropdownPosition = () => {
            const menuBar = menu.closest('.header-v2-desktop-bottom');
            const trigger = menu.firstElementChild?.querySelector('.header__menu-item') || menu;
            if (!menuBar || !trigger) return;

            const menuBarRect = menuBar.getBoundingClientRect();
            const triggerRect = trigger.getBoundingClientRect();

            menu.style.setProperty('--header-v2-dropdown-top', `${menuBarRect.bottom}px`);
            menu.style.setProperty('--header-v2-dropdown-left', `${triggerRect.left}px`);
        };

        const updateDropdownPositionAfterLayout = () => {
            updateDropdownPosition();
            window.requestAnimationFrame(updateDropdownPosition);
        };

        const updateScrollableMenuState = (scrollableMenu) => {
            if (!menu.classList.contains('is-dropdown-ready') || menu.classList.contains('is-dropdown-closing') || scrollableMenu.clientHeight <= 0) {
                scrollableMenu.classList.remove('has-scroll-more');
                return;
            }

            const scrollRemaining = scrollableMenu.scrollHeight - scrollableMenu.clientHeight - scrollableMenu.scrollTop;
            scrollableMenu.classList.toggle('has-scroll-more', scrollRemaining > 2);
        };

        const updateScrollableMenuStates = () => {
            scrollableMenus.forEach(updateScrollableMenuState);
        };

        const clearScrollableMenuStates = () => {
            scrollableMenus.forEach((scrollableMenu) => {
                scrollableMenu.classList.remove('has-scroll-more');
            });
        };

        const updateScrollableMenuStatesAfterLayout = () => {
            updateScrollableMenuStates();
            window.requestAnimationFrame(updateScrollableMenuStates);
        };

        const updateDropdownAfterLayout = () => {
            menu.classList.remove('is-dropdown-closing');
            updateDropdownPositionAfterLayout();
            updateScrollableMenuStatesAfterLayout();
        };

        menu.addEventListener('pointerenter', updateDropdownAfterLayout);
        menu.addEventListener('focusin', updateDropdownAfterLayout);
        window.addEventListener('resize', updateDropdownAfterLayout);
        window.addEventListener('scroll', updateDropdownAfterLayout, { passive: true });
        window.addEventListener('load', updateDropdownAfterLayout, { once: true });

        updateDropdownPosition();
        menu.classList.add('is-dropdown-ready');

        scrollableMenus.forEach((scrollableMenu) => {
            scrollableMenu.addEventListener('scroll', () => {
                updateScrollableMenuState(scrollableMenu);
            }, { passive: true });

            scrollableMenu.querySelectorAll('details').forEach((details) => {
                details.addEventListener('toggle', updateScrollableMenuStatesAfterLayout);
            });
        });

        const clearActiveSubmenuItems = () => {
            submenuItems.forEach((item) => {
                item.classList.remove('is-submenu-active');
            });
        };

        const closeDropdownState = () => {
            menu.classList.add('is-dropdown-closing');
            clearActiveSubmenuItems();
            clearScrollableMenuStates();

            window.setTimeout(() => {
                menu.classList.remove('is-dropdown-closing');
            }, 300);
        };

        const setActiveSubmenuItem = (item) => {
            clearActiveSubmenuItems();

            if (!item.querySelector(':scope > div > .menu-level-3')) return;

            item.classList.add('is-submenu-active');
            updateDropdownPositionAfterLayout();
            updateScrollableMenuStatesAfterLayout();
        };

        submenuItems.forEach((item) => {
            item.addEventListener('pointerenter', () => setActiveSubmenuItem(item));
            item.addEventListener('focusin', () => setActiveSubmenuItem(item));
        });

        menu.addEventListener('pointerleave', () => {
            clearActiveSubmenuItems();
            clearScrollableMenuStates();
        });
        menu.addEventListener('focusout', () => {
            window.setTimeout(() => {
                if (!menu.contains(document.activeElement)) {
                    clearActiveSubmenuItems();
                    clearScrollableMenuStates();
                }
            }, 0);
        });

        menu.addEventListener('click', (event) => {
            if (!event.target.closest('a[href]')) return;
            closeDropdownState();
        }, true);
    });
}

function getDirectChild(element, selector) {
    return Array.from(element?.children || []).find((child) => child.matches(selector)) || null;
}

function cloneHeaderDrawerIcon(sectionRoot, selector) {
    const template = sectionRoot.querySelector(selector);
    return template?.content.cloneNode(true) || document.createDocumentFragment();
}

function cloneHeaderMenuLabel(sourceLink) {
    const sourceLabel = getDirectChild(sourceLink, 'span.d-flex');
    if (sourceLabel) return sourceLabel.cloneNode(true);

    const label = document.createElement('span');
    label.className = 'd-flex align-items-center';
    label.textContent = (sourceLink?.textContent || '').trim();
    return label;
}

function createHeaderDrawerLink(sourceLink, className, id) {
    const link = document.createElement('a');
    link.id = id;
    link.href = sourceLink?.href || '#';
    link.className = className;
    link.appendChild(cloneHeaderMenuLabel(sourceLink));
    return link;
}

function createHeaderDrawerAccordionIcons(sectionRoot) {
    return cloneHeaderDrawerIcon(sectionRoot, 'template[data-header-drawer-accordion-icons]');
}

function buildHeaderDrawerLevelFour(sourceList, idPrefix) {
    const list = document.createElement('ul');
    list.id = `${idPrefix}-list`;
    list.className = 'menu-drawer__menu list-menu menu-level-4';
    list.setAttribute('role', 'list');
    list.tabIndex = -1;

    Array.from(sourceList?.children || []).forEach((sourceItem, index) => {
        const sourceLink = getDirectChild(sourceItem, 'a');
        if (!sourceLink) return;

        const item = document.createElement('li');
        item.appendChild(createHeaderDrawerLink(
            sourceLink,
            'menu-drawer__menu-item link link--text list-menu__item focus-inset great-grand-child-link',
            `${idPrefix}-${index}`,
        ));
        list.appendChild(item);
    });

    return list;
}

function buildHeaderDrawerLevelThree(sourceList, sectionRoot, idPrefix) {
    const list = document.createElement('ul');
    list.id = `${idPrefix}-list`;
    list.className = 'menu-drawer__menu list-menu menu-level-3';
    list.setAttribute('role', 'list');
    list.tabIndex = -1;

    Array.from(sourceList?.children || []).forEach((sourceItem, index) => {
        const item = document.createElement('li');
        const sourceDetails = getDirectChild(sourceItem, 'details');
        const itemId = `${idPrefix}-${index}`;

        if (!sourceDetails) {
            const sourceLink = getDirectChild(sourceItem, 'a');
            if (!sourceLink) return;
            item.appendChild(createHeaderDrawerLink(
                sourceLink,
                'menu-drawer__menu-item link link--text list-menu__item focus-inset grand-child-link',
                itemId,
            ));
            list.appendChild(item);
            return;
        }

        const sourceSummary = getDirectChild(sourceDetails, 'summary');
        const sourceLevelFour = getDirectChild(sourceDetails, 'ul.menu-level-4');
        const details = document.createElement('details');
        details.id = `Details-${itemId}`;

        const summary = document.createElement('summary');
        summary.id = itemId;
        summary.className = 'menu-drawer__menu-item link link--text list-menu__item focus-inset grand-child-link d-flex justify-content-between align-items-center';
        summary.appendChild(cloneHeaderMenuLabel(sourceSummary));
        summary.appendChild(createHeaderDrawerAccordionIcons(sectionRoot));

        const submenu = document.createElement('div');
        submenu.id = `${itemId}-submenu`;
        submenu.className = 'has-submenu gradient';
        submenu.appendChild(buildHeaderDrawerLevelFour(sourceLevelFour, `${itemId}-level-4`));

        details.append(summary, submenu);
        item.appendChild(details);
        list.appendChild(item);
    });

    return list;
}

function buildHeaderDrawerLevelTwo(sourceList, sectionRoot, idPrefix) {
    const list = document.createElement('ul');
    list.className = 'menu-drawer__menu list-menu menu-level-2';
    list.setAttribute('role', 'list');
    list.tabIndex = -1;

    Array.from(sourceList?.children || []).forEach((sourceItem, index) => {
        const item = document.createElement('li');
        const sourceSubmenu = getDirectChild(sourceItem, 'div');
        const sourceLink = sourceSubmenu ? getDirectChild(sourceSubmenu, 'a') : getDirectChild(sourceItem, 'a');
        const sourceLevelThree = sourceSubmenu ? getDirectChild(sourceSubmenu, 'ul.menu-level-3') : null;
        const itemId = `${idPrefix}-${index}`;
        if (!sourceLink) return;

        if (!sourceLevelThree) {
            item.appendChild(createHeaderDrawerLink(
                sourceLink,
                'menu-drawer__menu-item link link--text list-menu__item focus-inset',
                itemId,
            ));
            list.appendChild(item);
            return;
        }

        const details = document.createElement('details');
        details.id = `Details-${itemId}`;

        const summary = document.createElement('summary');
        summary.id = itemId;
        summary.className = 'menu-drawer__menu-item link link--text list-menu__item focus-inset d-flex justify-content-between align-items-center child-link';
        summary.appendChild(cloneHeaderMenuLabel(sourceLink));
        summary.appendChild(createHeaderDrawerAccordionIcons(sectionRoot));

        const submenu = document.createElement('div');
        submenu.id = `${itemId}-submenu`;
        submenu.className = 'has-submenu gradient';
        submenu.appendChild(buildHeaderDrawerLevelThree(sourceLevelThree, sectionRoot, `${itemId}-level-3`));

        details.append(summary, submenu);
        item.appendChild(details);
        list.appendChild(item);
    });

    return list;
}

function buildHeaderDrawerRoot(sourceRoot, sectionRoot, drawerId, blockId) {
    const sourceMenu = getDirectChild(sourceRoot, 'header-menu');
    if (!sourceMenu) return null;

    const sourceContainer = sourceMenu.firstElementChild;
    const sourceLink = getDirectChild(sourceContainer, 'a');
    const sourceLevelTwo = getDirectChild(sourceContainer, 'ul.menu-level-2');
    if (!sourceLink || !sourceLevelTwo) return null;

    const item = document.createElement('li');
    item.dataset.headerMenuRoot = blockId;

    const details = document.createElement('details');
    details.id = `Details-${drawerId}-${blockId}`;

    const summary = document.createElement('summary');
    summary.id = `HeaderDrawer-${drawerId}-${blockId}`;
    summary.className = 'menu-drawer__menu-item list-menu__item link link--text focus-inset justify-content-between';
    summary.append(document.createTextNode((sourceLink.textContent || '').trim()));
    summary.appendChild(cloneHeaderDrawerIcon(sectionRoot, 'template[data-header-drawer-arrow-icon]'));

    const submenu = document.createElement('div');
    submenu.id = `${drawerId}-link-${blockId}`;
    submenu.className = 'menu-drawer__submenu has-submenu gradient motion-reduce';
    submenu.tabIndex = -1;

    const inner = document.createElement('div');
    inner.className = 'menu-drawer__inner-submenu';

    const closeButton = document.createElement('button');
    closeButton.type = 'button';
    closeButton.className = 'menu-drawer__close-button link link--text focus-inset position-relative';
    closeButton.setAttribute('aria-expanded', 'true');
    closeButton.appendChild(cloneHeaderDrawerIcon(sectionRoot, 'template[data-header-drawer-arrow-icon]'));
    closeButton.append(document.createTextNode((sourceLink.textContent || '').trim()));

    inner.append(
        closeButton,
        buildHeaderDrawerLevelTwo(sourceLevelTwo, sectionRoot, `HeaderDrawer-${drawerId}-${blockId}-level-2`),
    );
    submenu.appendChild(inner);

    const scrollMore = document.createElement('span');
    scrollMore.className = 'menu-drawer__scroll-more menu-drawer__scroll-more--submenu';
    scrollMore.setAttribute('aria-hidden', 'true');
    scrollMore.textContent = 'Scroll for more';
    submenu.appendChild(scrollMore);

    details.append(summary, submenu);
    item.appendChild(details);
    return item;
}

function hydrateHeaderV2Drawer(drawer) {
    if (drawer.dataset.menuHydrated === 'true') return;

    const sectionRoot = drawer.closest('.section-header-v2');
    const sourceRoots = Array.from(sectionRoot?.querySelectorAll('.header__inline-menu [data-header-menu-source]') || []);
    if (!sectionRoot || sourceRoots.length === 0) return;

    const drawerId = drawer.querySelector('.menu-drawer')?.id || 'menu-drawer';
    drawer.querySelectorAll('[data-header-menu-root]').forEach((placeholder) => {
        const blockId = placeholder.dataset.headerMenuRoot;
        const sourceRoot = sourceRoots.find((source) => source.dataset.headerMenuSource === blockId);
        const hydratedRoot = sourceRoot && buildHeaderDrawerRoot(sourceRoot, sectionRoot, drawerId, blockId);
        if (hydratedRoot) placeholder.replaceWith(hydratedRoot);
    });

    drawer.dataset.menuHydrated = 'true';
    drawer.bindEvents?.();
    bindHeaderV2DrawerScrollMore(sectionRoot);
}

function bindDeferredHeaderV2Drawers(root = document) {
    root.querySelectorAll('.section-header-v2 header-drawer[data-deferred-menu-drawer]').forEach((drawer) => {
        if (drawer.dataset.deferredMenuBound === 'true') return;

        drawer.dataset.deferredMenuBound = 'true';
        const summary = drawer.querySelector(':scope > details > summary');
        if (!summary) return;

        const hydrate = () => hydrateHeaderV2Drawer(drawer);
        summary.addEventListener('pointerdown', hydrate, { once: true });
        summary.addEventListener('focus', hydrate, { once: true });
        summary.addEventListener('click', hydrate, { capture: true });
    });
}

function bindHeaderV2DrawerScrollMore(root = document) {
    root.querySelectorAll('.section-header-v2 header-drawer').forEach((drawer) => {
        const panel = drawer.querySelector('.menu-drawer');
        const getScrollContainers = () => drawer.querySelectorAll('.menu-drawer__navigation-container, .menu-drawer__inner-submenu');

        const updateDrawerAvailableHeight = () => {
            if (!panel) return;

            const viewportHeight = window.innerHeight || document.documentElement.clientHeight;
            const panelTop = Math.max(0, panel.getBoundingClientRect().top);
            const availableHeight = Math.max(240, viewportHeight - panelTop);

            panel.style.setProperty('--header-v2-drawer-available-height', `${availableHeight}px`);
        };

        const updateScrollableState = (scrollContainer) => {
            const scrollBadge = scrollContainer.nextElementSibling?.classList?.contains('menu-drawer__scroll-more')
                ? scrollContainer.nextElementSibling
                : null;

            if (scrollContainer.clientHeight <= 0) {
                scrollContainer.classList.remove('has-scroll-more');
                scrollBadge?.classList.remove('is-visible');
                return;
            }

            const scrollRemaining = scrollContainer.scrollHeight - scrollContainer.clientHeight - scrollContainer.scrollTop;
            const hasScrollMore = scrollRemaining > 2;

            scrollContainer.classList.toggle('has-scroll-more', hasScrollMore);
            scrollBadge?.classList.toggle('is-visible', hasScrollMore);
        };

        const updateScrollableStates = () => {
            updateDrawerAvailableHeight();
            getScrollContainers().forEach(updateScrollableState);
        };

        const clearScrollableStates = () => {
            getScrollContainers().forEach((scrollContainer) => {
                const scrollBadge = scrollContainer.nextElementSibling?.classList?.contains('menu-drawer__scroll-more')
                    ? scrollContainer.nextElementSibling
                    : null;

                scrollContainer.classList.remove('has-scroll-more');
                scrollBadge?.classList.remove('is-visible');
            });
        };

        const updateScrollableStatesAfterLayout = () => {
            updateScrollableStates();
            window.requestAnimationFrame(updateScrollableStates);
            window.setTimeout(updateScrollableStates, 450);
        };

        getScrollContainers().forEach((scrollContainer) => {
            if (scrollContainer.dataset.headerV2ScrollContainerBound === 'true') return;
            scrollContainer.dataset.headerV2ScrollContainerBound = 'true';
            scrollContainer.addEventListener('scroll', () => {
                updateScrollableState(scrollContainer);
            }, { passive: true });
        });

        drawer.querySelectorAll('details').forEach((details) => {
            if (details.dataset.headerV2ScrollDetailsBound === 'true') return;
            details.dataset.headerV2ScrollDetailsBound = 'true';
            details.addEventListener('toggle', () => {
                if (!details.open) {
                    clearScrollableStates();
                }

                updateScrollableStatesAfterLayout();
            });
        });

        drawer.querySelectorAll('summary, .menu-drawer__close-button').forEach((trigger) => {
            if (trigger.dataset.headerV2ScrollTriggerBound === 'true') return;
            trigger.dataset.headerV2ScrollTriggerBound = 'true';
            trigger.addEventListener('click', updateScrollableStatesAfterLayout);
        });

        drawer.querySelectorAll('a[href]').forEach((link) => {
            if (link.dataset.headerV2ScrollLinkBound === 'true') return;
            link.dataset.headerV2ScrollLinkBound = 'true';
            link.addEventListener('click', clearScrollableStates, true);
        });

        if (drawer.dataset.headerV2DrawerScrollBound !== 'true') {
            drawer.dataset.headerV2DrawerScrollBound = 'true';
            window.addEventListener('resize', updateScrollableStatesAfterLayout);
        }
        updateScrollableStatesAfterLayout();
    });
}

function clearHeaderV2ScrollMoreStates(root = document) {
    root.querySelectorAll('.section-header-v2 .has-scroll-more').forEach((element) => {
        element.classList.remove('has-scroll-more');
    });

    root.querySelectorAll('.section-header-v2 .menu-drawer__scroll-more.is-visible').forEach((element) => {
        element.classList.remove('is-visible');
    });

    root.querySelectorAll('.section-header-v2 .menu-level-1.is-dropdown-closing').forEach((element) => {
        element.classList.remove('is-dropdown-closing');
    });
}

window.addEventListener('pagehide', () => clearHeaderV2ScrollMoreStates());

if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => {
        bindHeaderV2SearchState();
        bindHeaderV2MobileNavDrag();
        bindHeaderV2DesktopDropdowns();
        bindDeferredHeaderV2Drawers();
        bindHeaderV2DrawerScrollMore();
    }, { once: true });
} else {
    bindHeaderV2SearchState();
    bindHeaderV2MobileNavDrag();
    bindHeaderV2DesktopDropdowns();
    bindDeferredHeaderV2Drawers();
    bindHeaderV2DrawerScrollMore();
}

document.addEventListener('shopify:section:load', () => {
    bindHeaderV2SearchState();
    bindHeaderV2MobileNavDrag();
    bindHeaderV2DesktopDropdowns();
    bindDeferredHeaderV2Drawers();
    bindHeaderV2DrawerScrollMore();
});
